import { get, writable } from 'svelte/store';

import { USER_DATA_DEFAULT } from 'src/constants';
import { extractUserTokenData } from 'src/services/auth_token';
import type { UserData, UserNotificationsData } from 'src/types/UserData';
import type { UserProfileData } from 'src/types/requests/services/user_profile_data';

function create_user_data() {
	const { subscribe, set, update } = writable<UserData>(
		JSON.parse(localStorage.getItem('user_data')) || USER_DATA_DEFAULT,
	);

	return {
		subscribe,
		set,
		update: (param_to_update: Partial<UserData>) =>
			update((current_params: UserData) => {
				if (typeof param_to_update.premium === 'string') {
					param_to_update.premium = Number(param_to_update.premium);
				}

				return {
					...current_params,
					...param_to_update,
				};
			}),
		remove: (prop_to_remove: keyof UserData) =>
			update((current_params: UserData) => {
				delete current_params[prop_to_remove];
				return current_params;
			}),
		reset: () => set(USER_DATA_DEFAULT),
		getNotificationUrls: () => [...new Set(get(user_data).notifications_urls)],
		getNotificationsData: (): UserNotificationsData => {
			return get(user_data)?.notifications_data || USER_DATA_DEFAULT.notifications_data;
		},
		getUserApiUrl: () => get(user_data).user_api_url,
		getEmail: () => (get(user_data)?.email ?? '').trim().toLowerCase(),
		getToken: () => get(user_data)?.token,
		getRefreshToken: () => get(user_data)?.refresh_token,
		updateTokens: (access_token: string, refresh_token: string) => {
			update((current_params: UserData) => {
				const tokenData = extractUserTokenData(access_token);
				return {
					...current_params,
					token: access_token,
					refresh_token,
					temporary: tokenData.temporary === true,
				};
			});
		},
	};
}

export const user_data = create_user_data();

user_data.subscribe((val: UserData) => {
	if (val) {
		localStorage.setItem('initializing_language', JSON.stringify(val.lang));
		localStorage.setItem('user_data', JSON.stringify(val));
	}
});

function initialUserData() {
	const { subscribe, set, update } = writable({});
	return {
		subscribe,
		set,
		update,
		reset: () => set({}),
	};
}

export const tempUserData = initialUserData();

export const home_data = writable(JSON.parse(localStorage.getItem('home_data')) || false);
home_data.subscribe((val) => localStorage.setItem('home_data', JSON.stringify(val)));

export const profile_data = writable<UserProfileData>(
	JSON.parse(localStorage.getItem('profile_data')) || {},
);
profile_data.subscribe((val) => localStorage.setItem('profile_data', JSON.stringify(val)));

export const user_settings = writable(JSON.parse(localStorage.getItem('user_settings')) || {});
user_settings.subscribe((val) => {
	if (val?.lang) localStorage.setItem('initializing_language', JSON.stringify(val.lang));
	localStorage.setItem('user_settings', JSON.stringify(val));
});
